/**
 * TypeScript compatible version of Boolean
 * Usage: myArrayWithPossibleNullOrUndefinedValues.filter(TsBoolean)
 * @param value - Any type
 * @returns The true if value is defined, false if value is null or undefined
 */
export const TsBoolean = <T>(value: T): value is NonNullable<T> => value !== null && value !== undefined;

/**
 * TypeScript compatible version of Object.values
 * Usage: TsObjectEntries(myObject)
 * @param obj - Record<key, value>
 * @returns A typed version of Object.entries (key & value pairs are perserved)
 */
export const TsObjectEntries = <T extends object>(obj: T): [keyof T, T[keyof T]][] => {
    return Object.entries(obj) as [keyof T, T[keyof T]][];
};

/**
 * Converts a string to a sha256 hashed string
 */
export const sha256 = async (input: string | undefined): Promise<string> => {
    if (!input) return "";

    const inputUint8 = new TextEncoder().encode(input);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", inputUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

    return hashHex;
};

/**
 * This type was provided by redux but was removed since v5, so it was added in our types
 */
export type DeepPartial<T> = {
    // eslint-disable-next-line @typescript-eslint/ban-types
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

/**
 * Returns the window location.
 * Use this function in cases where the window location should be mocked during testing
 */
export const getWindowLocation = (): Location => {
    return window.location;
};
